import type { ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { usePermissao } from '../hooks/use-permissao';
import { leituraDaTela } from '../lib/telas-acesso';
import { AcessoNegado } from './AcessoNegado';

/**
 * [ADR-069 / F4.4 #890, Decisão 1] Guarda de `/financeiro/titulos`, que serve as DUAS
 * telas RBAC (Contas a Pagar / Contas a Receber) por `?direcao=`. `RequerPermissaoRota`
 * genérico não basta aqui: ele só sabe checar "tem QUALQUER uma das permissões da lista",
 * não "tem a permissão DA DIREÇÃO que está na URL agora".
 *
 * `TitulosPage.tsx` (linha ~380) já trata ausência/valor inválido de `direcao` como
 * PAGAR — este guard espelha exatamente essa regra, pra nunca divergir do que a página
 * realmente abre.
 */
export function GuardDirecaoTitulos({ children }: { children: ReactNode }): JSX.Element {
  const { pode } = usePermissao();
  const [searchParams] = useSearchParams();

  const podePagar = pode(leituraDaTela('fin-pagar'));
  const podeReceber = pode(leituraDaTela('fin-receber'));

  if (!searchParams.has('direcao')) {
    // Sem direção explícita: a página abre em PAGAR por padrão. Quem só tem Receber
    // precisa ser mandado pra lá — senão bate numa URL sem direção que ele não pode ver,
    // mesmo tendo acesso à outra. Redirecionamento único de correção de default, não um
    // redirect por negação (não repete: já cai com `direcao` explícito na 2ª renderização).
    if (!podePagar && podeReceber) return <Navigate to="/financeiro/titulos?direcao=RECEBER" replace />;
    if (!podePagar && !podeReceber) return <AcessoNegado />;
    return <>{children}</>;
  }

  const direcaoAtual = searchParams.get('direcao') === 'RECEBER' ? 'RECEBER' : 'PAGAR';
  const podeDirecaoAtual = direcaoAtual === 'PAGAR' ? podePagar : podeReceber;
  if (!podeDirecaoAtual) return <AcessoNegado />;
  return <>{children}</>;
}
